import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { Row, Col, Grid } from 'react-material-responsive-grid';
import IndCircProgress from 'shared/components/IndeterminateCircularProgress';
import { TransitionGroup } from 'react-transition-group';
import FadeTransition from 'shared/components/FadeTransition';
import { Scrollbars } from 'react-custom-scrollbars';
import sharedStyles from 'shared/styles.css';
import ProductFilters from 'models/ProductFilters';
import ProductCardContainer from '../containers/ProductCardContainer';
import styles from './styles.css';

/**
 * Display a list of products as Product Cards in a flexbox.
 * @param {props} See Prop validation
 */
class ProductsDisplay extends React.Component {
	componentDidUpdate(prevProps) {
		const { categoryId, filters } = this.props;
		if (
			this._scrollbarsRef != null && // make sure _scrollbarsRef is defined before calling any of its methods
			(categoryId !== prevProps.categoryId || !isEqual(filters, prevProps.filters)) // scroll to top if category id or filters changed
		) {
			this._scrollbarsRef.scrollTop(0);
		}
	}

	/**
	 * If additional products are being fetched display an indeterminate
	 * circular progress bar.
	 * @param {boolean} isFetching
	 */
	loadProgressBar = isFetching => (isFetching ? <IndCircProgress /> : null);

	/**
	 * Ref callback for the Scrollbars component.
	 * When the Scrollbars component mounts, set scroll position to initialScrollPosition and save ref.
	 */
	initialScroll = ref => {
		if (ref != null) {
			ref.scrollTop(this.props.initialScrollPosition);
			this._scrollbarsRef = ref;
		}
	};

	render() {
		const {
			isFetching,
			products,
			scrollHandler,
			categoryId,
			displayProductsAndCategories,
		} = this.props;
		return products.length === 0 && isFetching ? (
			this.loadProgressBar(isFetching)
		) : (
			<div
				className={
					displayProductsAndCategories === true
						? sharedStyles.productSectionWithSubCategories
						: sharedStyles.productSection
				}
				id="infinite-scroll"
			>
				<Scrollbars ref={this.initialScroll} onScrollFrame={event => scrollHandler(event)}>
					<div className={sharedStyles.productScroll}>
						<Grid>
							<Row>
								{/* Fade in/fade out the product cards */}
								{products.map(product => (
									<Col
										key={product.id}
										xs4={4}
										xs8={4}
										sm12={4}
										md={4}
										lg={3}
										xl={3}
										className={sharedStyles.cardCol}
									>
										<TransitionGroup>
											<FadeTransition>
												<div className={styles['product-container']}>
													<ProductCardContainer
														id={product.id}
														name={product.name}
														price={product.price}
														msrp={product.msrp}
														oldPrice={product.oldPrice}
														pictureUrl={product.pictureUrl}
														sku={product.sku}
														categoryId={categoryId}
													/>
												</div>
											</FadeTransition>
										</TransitionGroup>
									</Col>
								))}
							</Row>
							{this.loadProgressBar(isFetching)}
						</Grid>
					</div>
				</Scrollbars>
			</div>
		);
	}
}

ProductsDisplay.propTypes = {
	categoryId: PropTypes.string,
	filters: PropTypes.instanceOf(ProductFilters).isRequired,
	initialScrollPosition: PropTypes.number,
	isFetching: PropTypes.bool.isRequired,
	displayProductsAndCategories: PropTypes.bool.isRequired,
	products: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
			price: PropTypes.number,
			pictureUrl: PropTypes.string,
			sku: PropTypes.string,
			oldPrice: PropTypes.number,
		}),
	).isRequired,
	scrollHandler: PropTypes.func.isRequired,
};

ProductsDisplay.defaultProps = {
	categoryId: '',
	initialScrollPosition: 0,
};

export default ProductsDisplay;
